// src/Services/nativeOpeners.js
/**
 * Native file open helpers for Android (Capacitor).
 *
 * These helpers save bytes into the app cache using Capacitor Filesystem and
 * then open with the platform default app via @capacitor-community/file-opener.
 * If no suitable app is installed, they fall back to the Share sheet.
 *
 * Usage: Prefer calling through window.openFile (wired in GlobalVariables.js),
 * which automatically routes to these on Android.
 *
 * Inputs can be Blob, ArrayBuffer, or base64 string.
 *
 * Notes:
 * - Uses Directory.Cache so scoped storage is respected.
 * - Filenames are sanitized for filesystem safety.
 * - Only used on native platforms; web uses a download fallback.
 *
 * @module Services/nativeOpeners
 */

import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { Share } from '@capacitor/share';

const ROOT = 'lytning'; // top-level cache folder for app-managed files

function sanitizeFilename(name) {
  const safe = (name || 'document').toString();
  return safe.replace(/[^\w.\-]/g, '_');
}

function abToBase64(ab) {
  // Convert ArrayBuffer to base64 in chunks to avoid call stack limits
  let binary = '';
  const bytes = new Uint8Array(ab);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  // btoa expects binary string
  return btoa(binary);
}

function isBase64String(s) {
  return typeof s === 'string' && /^[A-Za-z0-9+/=]+$/.test(s);
}

async function blobToBase64(b) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = /** @type {string} */ (reader.result || ''); // data:<mime>;base64,XXXX
      resolve(result.split(',')[1] || '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(b);
  });
}

async function toBase64(input) {
  if (input instanceof ArrayBuffer) return abToBase64(input);
  if (typeof Blob !== 'undefined' && input instanceof Blob) return blobToBase64(input);
  if (isBase64String(input)) return input;
  throw new Error('Unsupported input; expected Blob, ArrayBuffer, or base64 string.');
}

async function ensureParentFolder(relPath) {
  const slash = relPath.lastIndexOf('/');
  if (slash > 0) {
    const folder = relPath.substring(0, slash);
    try {
      await Filesystem.mkdir({ path: folder, directory: Directory.Cache, recursive: true });
    } catch (_) {
      // ignore if exists
    }
  }
}

async function writeAndResolveUri(base64, relPath) {
  // Make sure lytning/* parent folders exist
  await ensureParentFolder(relPath);

  await Filesystem.writeFile({
    path: relPath,
    data: base64,
    directory: Directory.Cache,
    recursive: true,
  });
  const { uri } = await Filesystem.getUri({ path: relPath, directory: Directory.Cache });
  return uri;
}

async function tryOpenOrShare(uri, mime, filename) {
  try {
    await FileOpener.open({ filePath: uri, contentType: mime, openWithDefault: true });
  } catch (err) {
    await Share.share({ title: filename, url: uri, dialogTitle: 'Open with…' });
  }
}

/**
 * Open a PDF natively on Android by saving to cache and routing to a viewer.
 * @param {Blob|ArrayBuffer|string} input
 * @param {string} filename e.g., 'report.pdf'
 */
export async function openPdfNative(input, filename = 'document.pdf') {
  const safe = sanitizeFilename(filename);
  const base64 = await toBase64(input);
  const rel = `${ROOT}/pdf/${Date.now()}-${safe}`;
  const uri = await writeAndResolveUri(base64, rel);
  await tryOpenOrShare(uri, 'application/pdf', safe);
}

/**
 * Open an Excel XLSX natively on Android.
 * @param {Blob|ArrayBuffer|string} input
 * @param {string} filename e.g., 'report.xlsx'
 */
export async function openExcelNative(input, filename = 'report.xlsx') {
  const safe = sanitizeFilename(filename);
  const base64 = await toBase64(input);
  const rel = `${ROOT}/xlsx/${Date.now()}-${safe}`;
  const uri = await writeAndResolveUri(base64, rel);
  await tryOpenOrShare(uri, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', safe);
}

/**
 * Generic native opener by MIME. Supports at least PDF and XLSX; others will
 * attempt to open via FileOpener with provided mime.
 * @param {Blob|ArrayBuffer|string} input
 * @param {string} mime
 * @param {string} filename
 */
export async function openFileNativeByMime(input, mime, filename) {
  const safe = sanitizeFilename(filename || 'document');
  const base64 = await toBase64(input);
  const folder = mime && mime.includes('sheet') ? 'xlsx' : (mime && mime.includes('pdf') ? 'pdf' : 'files');
  const rel = `${ROOT}/${folder}/${Date.now()}-${safe}`;
  const uri = await writeAndResolveUri(base64, rel);
  await tryOpenOrShare(uri, mime || 'application/octet-stream', safe);
}
